import { fmt } from '@/features/notas/calculo';
import type { Estatistica } from '@/features/notas/types';
import { Alvo, AreaGrafico, barraPath, COR } from './base';

/**
 * Distribuição da turma em faixas de 1 ponto. A faixa do aluno fica na cor da marca e as outras em neutro;
 * a linha fina marca a média da turma.
 */
export function Histograma({ estat, rotulo, altura = 200 }: { estat: Estatistica; rotulo: string; altura?: number }) {
  const max = Math.max(...estat.histograma, 1);
  const faixaAluno = estat.valorAluno === null ? -1 : Math.min(9, Math.floor(estat.valorAluno));
  const topo = 26;
  const base = altura - 24;
  return (
    <AreaGrafico altura={altura} rotulo={`${rotulo}: distribuição da turma`}>
      {(w) => {
        const esq = 26;
        const larg = w - esq - 4;
        const passo = larg / 10;
        const x = (v: number) => esq + (v / 10) * larg;
        const y = (n: number) => base - (n / max) * (base - topo);
        // marcas inteiras e regulares no eixo (0, 2, 4... ou 0, 5, 10...)
        const passoEixo = [1, 2, 5, 10, 20].find((p) => max / p <= 5) ?? 50;
        const marcas = Array.from({ length: Math.floor(max / passoEixo) + 1 }, (_, i) => i * passoEixo);
        return (
          <g>
            {marcas.map((m) => (
              <g key={m}>
                <line x1={esq} x2={w - 4} y1={y(m)} y2={y(m)} stroke={COR.grade} strokeWidth={1} />
                <text x={esq - 8} y={y(m)} dy="0.32em" textAnchor="end" fontSize={11} fill={COR.textoFraco}>
                  {m}
                </text>
              </g>
            ))}
            {estat.histograma.map((n, i) => {
              const ehAluno = i === faixaAluno;
              const bx = esq + i * passo + 1;
              const bw = passo - 2;
              const fim = i === 9 ? '10' : `${i + 1}`;
              return (
                <Alvo
                  key={i}
                  titulo={`${n} ${n === 1 ? 'aluno' : 'alunos'} com nota de ${i} a ${fim}${ehAluno ? ' (sua faixa)' : ''}`}
                >
                  <g style={{ cursor: 'default' }}>
                    <rect x={bx} y={topo - 10} width={bw} height={base - topo + 10} fill="transparent" />
                    {n > 0 && <path d={barraPath(bx, y(n), bw, base - y(n), 4)} fill={ehAluno ? COR.voce : COR.turma} />}
                  </g>
                </Alvo>
              );
            })}
            <line x1={esq} x2={w - 4} y1={base} y2={base} stroke={COR.turmaForte} strokeWidth={1} />
            {Array.from({ length: 11 }, (_, i) => (
              <text key={i} x={x(i)} y={base + 16} textAnchor="middle" fontSize={11} fill={COR.textoFraco}>
                {i}
              </text>
            ))}
            {/* média da turma */}
            <line x1={x(estat.media)} x2={x(estat.media)} y1={topo - 6} y2={base} stroke={COR.texto} strokeWidth={1.5} />
            <text x={x(estat.media)} y={topo - 12} textAnchor={estat.media > 8 ? 'end' : estat.media < 2 ? 'start' : 'middle'} fontSize={11} fill={COR.texto}>
              média {fmt(estat.media, 1)}
            </text>
            {faixaAluno >= 0 && (
              <text
                x={esq + faixaAluno * passo + passo / 2}
                y={y(estat.histograma[faixaAluno] ?? 0) - 6}
                textAnchor="middle"
                fontSize={11}
                fontWeight={700}
                fill={COR.texto}
              >
                você
              </text>
            )}
          </g>
        );
      }}
    </AreaGrafico>
  );
}
